import type { AgentActionResponse } from '@monorepo/shared/schemas/agents'
import { ACTION_TYPE } from '@monorepo/shared/schemas/common'
import type { ColumnDef } from '@tanstack/react-table'
import { Code, Edit, MoreHorizontal, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { SignalCodeDialog } from '@/shared/components/common/SignalCodeDialog'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import type { AgentActionFormData } from '../types/agent.types'

interface AgentActionsTableProps {
  actions: AgentActionFormData[] // Form data for display/editing
  rawActions: AgentActionResponse[] // Full API data for SignalCodeDialog
  onEdit: (action: AgentActionFormData) => void
  onDelete: (actionId: string) => void
}

const getActionTypeBadge = (actionType: string) => {
  switch (actionType) {
    case ACTION_TYPE.task:
      return (
        <Badge
          variant='secondary'
          className='bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
        >
          Task
        </Badge>
      )
    case ACTION_TYPE.workflow:
      return (
        <Badge
          variant='secondary'
          className='bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-200'
        >
          Workflow
        </Badge>
      )
    case ACTION_TYPE.outcome:
      return (
        <Badge
          variant='secondary'
          className='bg-slate-300 text-slate-900 dark:bg-slate-500 dark:text-slate-100'
        >
          Outcome
        </Badge>
      )
    default:
      return <Badge variant='secondary'>Unknown</Badge>
  }
}

export function AgentActionsTable({
  actions,
  rawActions,
  onEdit,
  onDelete,
}: AgentActionsTableProps) {
  const [selectedAction, setSelectedAction] =
    useState<AgentActionResponse | null>(null)
  const [deleteAction, setDeleteAction] = useState<AgentActionFormData | null>(
    null
  )

  // Create a map of action ID → raw action for quick lookup
  const rawActionsMap = useMemo(() => {
    const map = new Map<string, AgentActionResponse>()
    rawActions.forEach(action => {
      map.set(action.id, action)
    })
    return map
  }, [rawActions])

  const handleConfirmDelete = () => {
    if (deleteAction) {
      onDelete(deleteAction.id || deleteAction.signal_code)
      setDeleteAction(null)
    }
  }

  const columns: ColumnDef<AgentActionFormData>[] = [
    {
      accessorKey: 'action_name',
      header: 'Action Name',
      cell: ({ row }) => {
        const action = row.original
        return <div className='font-medium'>{action.action_name}</div>
      },
      size: 200,
    },
    {
      accessorKey: 'signal_code',
      header: 'Signal Code',
      cell: ({ row }) => {
        const action = row.original
        return (
          <code className='text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-300'>
            {action.signal_code}
          </code>
        )
      },
      size: 180,
    },
    {
      accessorKey: 'action_type',
      header: 'Type',
      cell: ({ row }) => {
        const action = row.original
        return getActionTypeBadge(action.action_type)
      },
      size: 100,
    },
    {
      accessorKey: 'aggregation_type',
      header: 'Aggregation',
      cell: ({ row }) => {
        const action = row.original
        return (
          <Badge variant='outline' className='capitalize'>
            {action.aggregation_type.split('_').join(' ')}
          </Badge>
        )
      },
      size: 120,
    },
    {
      accessorKey: 'unit_label',
      header: 'Unit',
      cell: ({ row }) => {
        const action = row.original
        return (
          <div className='text-sm text-muted-foreground'>
            {action.unit_label || '-'}
          </div>
        )
      },
      size: 100,
    },
    {
      accessorKey: 'revenue_per_unit',
      header: 'Revenue/Unit',
      cell: ({ row }) => {
        const action = row.original
        return (
          <div className='font-medium'>
            $
            {action.revenue_per_unit
              ? Number(action.revenue_per_unit).toFixed(2)
              : '0.00'}
          </div>
        )
      },
      size: 120,
    },
    {
      accessorKey: 'cost_signal_ids',
      header: 'Cost Signals',
      cell: ({ row }) => {
        const action = row.original
        return (
          <div className='text-center'>
            <span className='font-medium'>
              {action.cost_signal_ids ? action.cost_signal_ids.length : 0}
            </span>
          </div>
        )
      },
      size: 120,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const action = row.original
        return (
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                  <span className='sr-only'>Open menu</span>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onClick={() => onEdit(action)}>
                  <Edit className='mr-2 h-4 w-4' />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    const rawAction = action.id
                      ? rawActionsMap.get(action.id)
                      : null
                    if (rawAction) setSelectedAction(rawAction)
                  }}
                >
                  <Code className='mr-2 h-4 w-4' />
                  View Code
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant='destructive'
                  onClick={() => setDeleteAction(action)}
                >
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
      size: 50,
    },
  ]

  if (actions.length === 0) {
    return (
      <div className='border rounded-lg p-8 text-center text-muted-foreground'>
        No actions created yet. Add your first action to get started.
      </div>
    )
  }

  return (
    <>
      {selectedAction && (
        <SignalCodeDialog
          signal={selectedAction}
          open={!!selectedAction}
          onOpenChange={open => !open && setSelectedAction(null)}
        />
      )}

      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          data={actions}
          columns={columns}
          height='600px'
          className='bg-transparent border-0'
        />
      </div>

      <ConfirmationDialog
        open={!!deleteAction}
        onOpenChange={open => !open && setDeleteAction(null)}
        title='Delete Agent Action'
        description={`Are you sure you want to delete "${deleteAction?.action_name}"? This action cannot be undone and will permanently remove the action and all its associated data.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteAction(null)}
      />
    </>
  )
}
